import React, { memo, useMemo, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import orderBy from 'lodash/orderBy';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';

import { useAction } from 'app/hooks';
import { formatDateTime } from 'app/formatters';
import {
  actions as aisVersionsActions,
  selectors as aisVersionsSelectors,
} from 'ducks/aisVersions';
import { List, Breadcrumbs } from 'components';
import AisVersionFilter from './AisVersionFilter';

const AisVersionCheckedAtCell = memo(({ item: { aisVersionCheckedAt } }) => {
  const { t } = useTranslation();

  if (!aisVersionCheckedAt) {
    return <>{t('aisVersions:neverChecked')}</>;
  }

  return <>{formatDateTime(aisVersionCheckedAt)}</>;
});

AisVersionCheckedAtCell.propTypes = {
  item: PropTypes.shape({
    aisVersionCheckedAt: PropTypes.string,
  }).isRequired,
};

const AisVersionEnabledCell = memo(({ item: { enabled } }) => {
  const { t } = useTranslation();

  return <>{t(enabled ? 'app:yes' : 'app:no')}</>;
});

AisVersionEnabledCell.propTypes = {
  item: PropTypes.shape({
    enabled: PropTypes.bool,
  }).isRequired,
};

const rowConfig = [
  {
    name: 'number',
    source: 'number',
    label: 'aisVersions:storeNumber',
    sortable: true,
  },
  {
    name: 'name',
    source: 'name',
    label: 'aisVersions:storeName',
    sortable: true,
  },
  {
    name: 'enabled',
    label: 'aisVersions:storeEnabled',
    component: AisVersionEnabledCell,
    sortable: true,
  },
  {
    name: 'aisVersion',
    label: 'aisVersions:aisVersion',
    render: ({ aisVersion }) => aisVersion || '—',
    sortable: true,
  },
  {
    name: 'aisVersionCheckedAt',
    label: 'aisVersions:lastChecked',
    component: AisVersionCheckedAtCell,
    sortable: true,
  },
];

const sortAccessors = {
  number: (item) => item.number,
  name: (item) => (item.name || '').toLowerCase(),
  enabled: (item) => (item.enabled ? 1 : 0),
  aisVersion: (item) => (item.aisVersion || '').toLowerCase(),
  aisVersionCheckedAt: (item) =>
    item.aisVersionCheckedAt ? new Date(item.aisVersionCheckedAt).getTime() : 0,
};

const filterItems = (items, filter) => {
  const { name = '', number = '' } = filter;
  if (!name && !number) {
    return items;
  }
  return items.filter((item) => {
    if (number && !`${item.number}`.includes(number)) {
      return false;
    }
    if (name && !(item.name || '').toLowerCase().includes(name.toLowerCase())) {
      return false;
    }
    return true;
  });
};

const AisVersionList = memo(() => {
  const items = useSelector(aisVersionsSelectors.aisVersionList);
  const fetchAisVersionList = useAction(aisVersionsActions.fetchAisVersionList);
  const checkAisVersions = useAction(aisVersionsActions.checkAisVersions);
  const exportAisVersions = useAction(aisVersionsActions.exportAisVersions);
  const [sort, setSort] = useState({});
  const [filter, setFilter] = useState({});
  const hasFetchedRef = useRef(false);

  const filteredItems = useMemo(() => filterItems(items, filter), [items, filter]);

  const displayItems = useMemo(() => {
    const [field, direction] = Object.entries(sort)[0] || [];
    if (!field || !sortAccessors[field]) {
      return filteredItems;
    }
    return orderBy(filteredItems, [sortAccessors[field]], [direction]);
  }, [filteredItems, sort]);

  const meta = useMemo(
    () => ({ page: 1, limit: displayItems.length || 1, total: displayItems.length }),
    [displayItems.length],
  );

  // the AIS versions API always returns the full list with no sort/filter support,
  // so sorting and filtering are applied client-side based on URL state
  const onFetchData = useCallback(
    (params) => {
      setFilter(params?.filter || {});
      setSort(params?.sort || {});
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        fetchAisVersionList();
      }
    },
    [fetchAisVersionList],
  );

  const toolbarActions = useMemo(
    () => [
      {
        name: 'checkNow',
        label: 'aisVersions:checkNow',
        icon: RefreshIcon,
        confirm: false,
        callback: () => checkAisVersions(),
      },
      {
        name: 'exportAisVersions',
        label: 'aisVersions:export',
        icon: GetAppIcon,
        confirm: false,
        callback: () => exportAisVersions(),
      },
    ],
    [checkAisVersions, exportAisVersions],
  );

  return (
    <>
      <Breadcrumbs />
      <List
        data={displayItems}
        meta={meta}
        onFetchData={onFetchData}
        filterComponent={AisVersionFilter}
        rowConfig={rowConfig}
        rowIdSource="number"
        toolbarActions={toolbarActions}
      />
    </>
  );
});

export default AisVersionList;
